"use client"

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import mixpanel from 'mixpanel-browser'
import { useAuth } from './auth-context'
import { useLanguage } from './language-context'
import { decodeJwtToken } from '@/lib/jwt-utils'

type TrackProps = {
  name: string
  properties?: any
  trackPage?: boolean
}

interface ITracking {
  track: (event: TrackProps) => void
}

interface IButtonTrackingPayload {
  text: string
  type: string
  trackingEventName?: string
}

const TrackingContext = createContext<ITracking>({} as ITracking)

interface UserInfo {
  id: string
  firstName: string
  lastName: string
  email: string
  fullName: string
  tenantName: string
  environmentName: string
  appCode: string
}

/**
 * Extracts user information from a JWT token
 */
function extractUserFromToken(token: string): UserInfo | null {
  try {
    const payload = decodeJwtToken(token)
    if (!payload) {
      console.warn('Could not decode JWT token for user extraction')
      return null
    }

    // Extract user information based on your JWT structure
    const profileId = payload.SecurityContactProfileId || ''
    const userName = payload.SecurityContactUserName || ''
    const tenantName = payload.TenantName || ''
    const environmentName = payload.EnvironmentName || ''
    const appCode = payload.AppCode || ''

    // Extract email from SecurityContactUserName
    const email = userName || 'user@example.com'

    // Create user ID: SecurityContactProfileId
    const userId = profileId ? profileId : email

    // Extract name from email (fallback)
    // TODO: Get first name and last name from the payload
    const emailName = email.split('@')[0] || 'User'
    const firstName = null 
    const lastName = null
    const fullName = null

    return {
      id: userId,
      firstName: firstName || 'User',
      lastName: lastName || 'Name',
      email: email || 'user@example.com',
      fullName: fullName || 'User Name',
      tenantName: tenantName || 'Unknown',
      environmentName: environmentName || 'Unknown',
      appCode: appCode || 'Unknown'
    }
  } catch (error) {
    console.error('Error extracting user from JWT token:', error)
    return null
  }
}

export const generateUniqueID = (
  userId: string,
  email: string
): string => {
  return `${userId} - ${email}`
}

export const TrackingProvider: React.FC<{
  children: React.ReactNode
  token: string
  pageTitlePrefix?: string | null
}> = ({ children, token, pageTitlePrefix = null }) => {
  const { token: authToken, isAuthenticated } = useAuth()
  const { language } = useLanguage()
  const [panelLoaded, setPanelLoaded] = useState(false)

  // Common properties for tracking
  const commonProperties = useMemo(() => ({
    language,
    timestamp: new Date().toISOString(),
    userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : '',
    url: typeof window !== 'undefined' ? window.location.href : '',
  }), [language])

  // Extract real user data from JWT token
  const currentUser = useMemo(() => {
    if (!isAuthenticated || !authToken) return null
    
    // Extract user information from the JWT token
    const userInfo = extractUserFromToken(authToken)
    if (!userInfo) {
      return {
        id: 'unknown-user',
        firstName: 'User',
        lastName: 'Name',
        email: 'user@example.com', 
        tenantName: 'Unknown',
        environmentName: 'Unknown',
        appCode: 'Unknown'
      }
    }
    
    return userInfo
  }, [isAuthenticated, authToken])

  // Mixpanel Configuration
  useEffect(() => {
    try {
      mixpanel.init(token, {
        persistence: 'localStorage',
      })

      if (currentUser) {
        const { id, firstName, lastName, email, tenantName, environmentName, appCode } = currentUser
        const uniqueID = generateUniqueID(id, email)
        mixpanel.identify(uniqueID)
        
        // default properties
        mixpanel.register({
          $distinct_id: uniqueID,          
          tenantName: tenantName,
          environmentName: environmentName,
          appCode: appCode,
          ...commonProperties,
        })

        // register user with mixpanel
        mixpanel.people.set({
          userId: id,
          $distinct_id: uniqueID,
          $email: email,
          $first_name: firstName,
          $last_name: lastName,
        })
      }

      setPanelLoaded(true)
    } catch (error) {
      console.error('Failed to initialize Mixpanel:', error)
    }
  }, [commonProperties, currentUser, token])

  // Track Events
  const track = useCallback((event: TrackProps) => {
    const { name, properties } = event
    try {
      mixpanel.track(name, {
        ...properties,
        ...commonProperties,
      })
    } catch (error) {
      console.error('Failed to track event:', error)
    }
  }, [commonProperties])

  // Track PageViews
  useEffect(() => {
    if (panelLoaded && typeof window !== 'undefined') {
      try {
        const locationPathName = window.location.pathname.split('/')[1]
        let pageTitle = locationPathName?.trim()?.replace(/-/g, ' ') || 'Home'
        if (pageTitlePrefix) {
          pageTitle = `${pageTitlePrefix} - ${pageTitle}`
        }
        
        // Update document title
        document.title = pageTitle
        
        mixpanel.track_pageview()
      } catch (err) {
        console.error('Failed to track pageview:', err)
      }
    }
  }, [panelLoaded, pageTitlePrefix])

  // Click Tracking
  const handleClickEvent = useCallback((event: MouseEvent) => {
    try {
      const link = (event.target as HTMLElement).closest(`a`)
      if (link) {
        mixpanel.track('Click Tracking', {
          text: link.ariaLabel ? link.ariaLabel : link.textContent,
          type: 'Link',
          url: link.href,
        })
      }

      const button = (event.target as HTMLElement).closest(`button`)
      if (button) {
        if (button.textContent) {
          let payload: IButtonTrackingPayload = {
            text: button.textContent,
            type: 'Button',
          }
          if (button.name) {
            payload = {
              ...payload,
              trackingEventName: button.name,
            }
          }
          mixpanel.track('Click Tracking', payload)
        } else if (button.ariaLabel) {
          // Icon Button
          mixpanel.track('Click Tracking', {
            text: button.ariaLabel,
            type: 'IconButton',
          })
        }
      }

      const listItem = (event.target as HTMLElement).closest(`li`)
      if (listItem) {
        mixpanel.track('Click Tracking', {
          text: listItem.textContent,
          type: 'MenuItem',
        })
      }
    } catch (error) {
      console.error('Failed to track click event:', error)
    }
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      document.addEventListener('click', handleClickEvent)
      return () => {
        document.removeEventListener('click', handleClickEvent)
      }
    }
  }, [handleClickEvent])

  const mixPanelTrack = useMemo(() => {
    return { track }
  }, [track])

  return (
    <TrackingContext.Provider value={mixPanelTrack}>
      {children}
    </TrackingContext.Provider>
  )
}

export const useTracking = (): ITracking => {
  const context = useContext(TrackingContext)
  if (!context) {
    throw new Error('useTracking must be used within a TrackingProvider')
  }
  return context
} 